/**
 * Admin-editable AI system prompts as tagged DocumentReferences.
 *
 * Prompt text lives in `content[0].attachment.data` (base64 UTF-8, text/plain).
 * Discovery uses DocumentReference.meta.tag (kind + subject).
 * Hosts (HealthcareService, Organization, Questionnaire) do not carry prompt extensions.
 */
import type { Coding, DocumentReference, Resource } from '@medplum/fhirtypes';

import {
  AI_PROMPT_BINARY_CONTENT_TYPE,
  AI_PROMPT_KIND_SYSTEM,
  AI_PROMPT_SUBJECT_SYSTEM,
  AI_PROMPT_TAG,
  AI_PROMPT_TAG_CODE,
  UNIFY_TAG_SYSTEM,
  type AiPromptKind,
} from './constants';
import { withUnifyProfile } from './profiles/with-unify-profile';

/** Read half of the prompt store: what a read-only bot (scoped reader) needs to load a custom prompt. */
export type AiPromptReadClient = Pick<AiPromptBinaryClient, 'searchResources'>;

/** Minimal FHIR client surface used by the prompt store (MedplumClient-compatible). */
export interface AiPromptBinaryClient {
  searchResources(resourceType: 'DocumentReference', query: string): Promise<DocumentReference[]>;
  createResource<T extends Resource>(resource: T): Promise<T>;
  updateResource<T extends Resource>(resource: T): Promise<T>;
}

export function normalizeAiPromptSubjectRef(subjectRef: string): string {
  return subjectRef.trim().replace(/^\/+/, '');
}

export function buildAiPromptBinaryTags(kind: AiPromptKind, subjectRef: string): Coding[] {
  const subject = normalizeAiPromptSubjectRef(subjectRef);
  return [
    { ...AI_PROMPT_TAG },
    { system: AI_PROMPT_KIND_SYSTEM, code: kind, display: kind },
    { system: AI_PROMPT_SUBJECT_SYSTEM, code: subject, display: subject },
  ];
}

/** FHIR search query for DocumentReferences matching kind + subject (AND on tags). */
export function buildAiPromptSearchQuery(kind: AiPromptKind, subjectRef: string): string {
  const subject = normalizeAiPromptSubjectRef(subjectRef);
  const tags = [
    `${UNIFY_TAG_SYSTEM}|${AI_PROMPT_TAG_CODE}`,
    `${AI_PROMPT_KIND_SYSTEM}|${kind}`,
    `${AI_PROMPT_SUBJECT_SYSTEM}|${subject}`,
  ];
  return `${tags.map((tag) => `_tag=${encodeURIComponent(tag)}`).join('&')}&status=current&_count=20`;
}

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** Pure standard base64 (with padding) for runtimes with no `btoa`; see {@link decodeBase64Manually}. */
export function encodeBase64Manually(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    out += BASE64_ALPHABET[(n >> 18) & 63]!;
    out += BASE64_ALPHABET[(n >> 12) & 63]!;
    out += i + 1 < bytes.length ? BASE64_ALPHABET[(n >> 6) & 63]! : '=';
    out += i + 2 < bytes.length ? BASE64_ALPHABET[n & 63]! : '=';
  }
  return out;
}

function utf8BytesToBase64(bytes: Uint8Array): string {
  if (typeof btoa !== 'function') return encodeBase64Manually(bytes);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/**
 * Pure decoder for runtimes with no `atob` (the Medplum `vmcontext` bot sandbox has neither `atob`,
 * `btoa`, nor `Buffer`; it does have `TextDecoder`, checked on local Docker). Follows the WHATWG
 * forgiving-base64 rules that `atob` implements: ASCII whitespace is ignored anywhere, a length that
 * is a multiple of 4 may end in one or two `=`, a remainder of 1 is invalid, and `=` anywhere else
 * is invalid. Exported for the parity tests; throws on invalid input.
 */
export function decodeBase64Manually(base64: string): Uint8Array {
  let clean = base64.replace(/[\t\n\f\r ]+/g, '');
  if (clean.length % 4 === 0) clean = clean.replace(/={1,2}$/, '');
  if (clean.length % 4 === 1) throw new Error('Invalid base64 length');
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of clean) {
    const value = BASE64_ALPHABET.indexOf(char);
    if (value < 0) throw new Error('Invalid base64 character');
    buffer = ((buffer << 6) | value) & 0xffffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return Uint8Array.from(bytes);
}

function base64ToUtf8Bytes(base64: string): string {
  if (typeof atob !== 'function') return new TextDecoder().decode(decodeBase64Manually(base64));
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

export function encodeAiPromptBinaryData(text: string): string {
  return utf8BytesToBase64(new TextEncoder().encode(text));
}

/**
 * General names for the same codecs, for inline `Attachment.data` such as session transcripts: real
 * UTF-8 (4-byte sequences for supplementary-plane characters) with no `btoa`/`atob`/`Buffer`
 * requirement. The decoder throws on invalid base64.
 */
export const utf8TextToBase64 = encodeAiPromptBinaryData;
export { base64ToUtf8Bytes as base64ToUtf8Text };

export function decodeAiPromptAttachmentData(data: string | undefined): string | undefined {
  const trimmed = data?.trim();
  if (!trimmed) return undefined;
  try {
    return base64ToUtf8Bytes(trimmed).trim() || undefined;
  } catch {
    return undefined;
  }
}

export function buildAiPromptDocumentReference(
  kind: AiPromptKind,
  subjectRef: string,
  text: string,
): DocumentReference {
  const subject = normalizeAiPromptSubjectRef(subjectRef);
  const tags = buildAiPromptBinaryTags(kind, subject);
  return withUnifyProfile({
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [{ ...AI_PROMPT_TAG }],
      text: 'AI Prompt',
    },
    description: `AI system prompt (${kind}) for ${subject}`,
    content: [
      {
        attachment: {
          contentType: AI_PROMPT_BINARY_CONTENT_TYPE,
          data: encodeAiPromptBinaryData(text),
          title: `${kind} prompt`,
        },
      },
    ],
    meta: { tag: tags },
  });
}

export async function searchAiPromptDocumentReferences(
  client: AiPromptReadClient,
  kind: AiPromptKind,
  subjectRef: string,
): Promise<DocumentReference[]> {
  const matches = await client.searchResources('DocumentReference', buildAiPromptSearchQuery(kind, subjectRef));
  return matches.filter((doc) => doc.status !== 'entered-in-error');
}

function readPromptTextFromDocumentReference(doc: DocumentReference): string | undefined {
  for (const entry of doc.content ?? []) {
    const text = decodeAiPromptAttachmentData(entry.attachment?.data);
    if (text) return text;
  }
  return undefined;
}

/** Returns the custom prompt text, or undefined when none is configured. */
export async function resolveAiPromptText(
  client: AiPromptReadClient,
  kind: AiPromptKind,
  subjectRef: string,
): Promise<string | undefined> {
  const matches = await searchAiPromptDocumentReferences(client, kind, subjectRef);
  for (const doc of matches) {
    const text = readPromptTextFromDocumentReference(doc);
    if (text) return text;
  }
  return undefined;
}

/** Admin policy forbids delete, so a cleared prompt is retired as entered-in-error. */
async function retireAiPromptDocumentReferences(
  client: AiPromptBinaryClient,
  docs: DocumentReference[],
): Promise<void> {
  for (const doc of docs) {
    if (doc.id) {
      await client.updateResource<DocumentReference>({ ...doc, status: 'entered-in-error' });
    }
  }
}

/** Retires all matching prompt DocumentReferences for kind + subject. */
export async function clearAiPrompt(
  client: AiPromptBinaryClient,
  kind: AiPromptKind,
  subjectRef: string,
): Promise<void> {
  const existing = await searchAiPromptDocumentReferences(client, kind, subjectRef);
  await retireAiPromptDocumentReferences(client, existing);
}

/**
 * Saves a custom prompt as a tagged DocumentReference with attachment.data.
 * Updates the existing prompt in place (tags preserved) or creates one, and retires
 * any duplicates. Blank / whitespace-only text clears the prompt.
 */
export async function saveAiPromptText(
  client: AiPromptBinaryClient,
  kind: AiPromptKind,
  subjectRef: string,
  text: string | undefined,
): Promise<DocumentReference | undefined> {
  const trimmed = text?.trim();
  const existing = await searchAiPromptDocumentReferences(client, kind, subjectRef);
  if (!trimmed) {
    await retireAiPromptDocumentReferences(client, existing);
    return undefined;
  }
  const next = buildAiPromptDocumentReference(kind, subjectRef, trimmed);
  const [primary, ...duplicates] = existing;
  if (!primary?.id) {
    return client.createResource(next);
  }
  const updated = await client.updateResource<DocumentReference>({
    ...primary,
    status: 'current',
    content: next.content,
    meta: { ...primary.meta, tag: next.meta?.tag },
  });
  await retireAiPromptDocumentReferences(client, duplicates);
  return updated;
}

/** Test/fixture helper: DocumentReference with inline attachment.data + mock search key. */
export function buildAiPromptStoreFixtures(
  kind: AiPromptKind,
  subjectRef: string,
  text: string,
  ids?: { docId?: string },
): { document: DocumentReference; searchKey: string } {
  const docId = ids?.docId ?? `doc-${kind}`;
  const document: DocumentReference = {
    ...buildAiPromptDocumentReference(kind, subjectRef, text),
    id: docId,
  };
  return {
    document,
    searchKey: `DocumentReference?${buildAiPromptSearchQuery(kind, subjectRef)}`,
  };
}
